/**
 * =============================================================================
 * IDS CRM Web - Pipeline Page
 * =============================================================================
 *
 * PURPOSE:
 * Pipeline management with sortable table view, inline stage editing,
 * Kanban board view, and Contracts Meeting watchlist.
 *
 * Orders (spec part B, 2026-10-16) have their own tab. The Opportunities and
 * Pipeline tabs and their totals leave orders out (an order is not an
 * opportunity); the Orders tab's total is the order book.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MarkLostModal from '../components/MarkLostModal';
import ScheduleEntryModal from '../components/ScheduleEntryModal';
import StageChangeDialog from '../components/StageChangeDialog';
import { formatMoney } from '../utils/money';
import { canManageDeals, canMoveDealStage } from '../utils/permissions';
import { addDays, formatDateKey, toDateKey } from '../utils/dates';
import { useListMemory, useRememberList } from '../hooks/useListMemory';
import { stagesForLine } from '../utils/orders';
import './Pipeline.css';
import './CommercialLayout.css';

// R5a (DL-11): closed groups carry a computed outcome badge
const OUTCOME_META = { won: { label: 'Won', cls: 'chip-green' }, partially_won: { label: 'Partially won', cls: 'chip-amber' }, lost: { label: 'Lost', cls: 'chip-red' } };

function Pipeline() {
  const { authFetch, user } = useAuth();
  const canCreate = canManageDeals(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  // Coming back from an opportunity or a line item (a "←" link or the
  // browser's Back) shows the page as it was left: the tab, its filters, the
  // search and the sort. A fresh visit starts from the address.
  const memory = useListMemory('/pipeline');

  const [opportunities, setOpportunities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [stages, setStages] = useState({});
  const [allStages, setAllStages] = useState([]);
  const [loading, setLoading] = useState(true);
  // Three tabs (spec §20): 'opportunities' (grouped, default), 'list'
  // ("Pipeline" — line items), 'watchlist' ("Contracts Meeting").
  const [activeTab, setActiveTab] = useState(() => memory.initial('tab', searchParams.get('tab') || 'opportunities'));
  const [watchlistSummary, setWatchlistSummary] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupStatusFilter, setGroupStatusFilter] = useState(() => memory.initial('groupStatus', 'active'));
  // Orders tab: whole orders (every order, whatever its lines) or order lines
  // (stage moves). Gareth 2026-10-19: no order may go missing from the tab.
  const [ordersView, setOrdersView] = useState(() => memory.initial('ordersView', 'orders'));
  const [groupByOpportunity, setGroupByOpportunity] = useState(() => memory.initial('groupByOpportunity', false));
  const [lostModalItem, setLostModalItem] = useState(null);
  const [sortField, setSortField] = useState(() => memory.initial('sort', searchParams.get('sort') || 'created_at'));
  const [sortOrder, setSortOrder] = useState(() => memory.initial('order', searchParams.get('order') || 'desc'));
  const [search, setSearch] = useState(() => memory.initial('search', searchParams.get('search') || ''));
  const customerId = searchParams.get('customer_id') || '';
  // Phase 12: every stage change answers the accountability dialog (which
  // also absorbs the old won-confirm); Phase 10's schedule modal chains after
  // it. Both are 400-driven — the server is the single source of truth.
  const [stageDialog, setStageDialog] = useState(null);
  const [stageDialogSubmitting, setStageDialogSubmitting] = useState(false);
  const [stageDialogError, setStageDialogError] = useState(null);
  const [scheduleModal, setScheduleModal] = useState(null);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  // Full filter set (the API already supported these - the UI didn't)
  const [stageFilter, setStageFilter] = useState(() => memory.initial('stage', searchParams.get('stage') || ''));
  const [productFilter, setProductFilter] = useState(() => memory.initial('product', searchParams.get('product') || ''));
  const [myDealsOnly, setMyDealsOnly] = useState(() => memory.initial('mine', searchParams.get('mine') === 'true'));
  const [watchlistOnly, setWatchlistOnly] = useState(() => memory.initial('watch', searchParams.get('watch') === 'true'));
  // The Contracts Meeting RAG filter. Held here, not in the board, so it is
  // remembered with the rest of the page.
  const [watchlistFilter, setWatchlistFilter] = useState(() => memory.initial('watchlistPriority', 'all'));
  // Failed actions surface here instead of dying in console.error
  const [pageError, setPageError] = useState(null);

  useRememberList('/pipeline', {
    tab: activeTab,
    groupStatus: groupStatusFilter,
    ordersView,
    search,
    product: productFilter,
    stage: stageFilter,
    mine: myDealsOnly,
    watch: watchlistOnly,
    groupByOpportunity,
    sort: sortField,
    order: sortOrder,
    watchlistPriority: watchlistFilter
  });

  // Fetch pipeline stages
  const fetchStages = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/opportunities/pipeline-stages');
      const data = await response.json();
      setStages(data.data || {});

      // Flatten all stages for dropdown
      const flat = [];
      Object.values(data.data || {}).forEach(stageList => {
        stageList.forEach(s => {
          if (!flat.find(f => f.stage_name === s.stage_name)) {
            flat.push(s);
          }
        });
      });
      setAllStages(flat);
    } catch (error) {
      console.error('Failed to fetch stages:', error);
    }
  }, [authFetch]);

  // Fetch opportunities
  const fetchOpportunities = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        open: 'true',
        limit: '100',
        sort: sortField,
        order: sortOrder
      });

      if (search) {
        params.append('search', search);
      }
      if (customerId) {
        params.append('customer_id', customerId);
      }
      if (stageFilter) params.append('stage', stageFilter);
      if (productFilter) params.append('product', productFilter);
      if (myDealsOnly && user?.userId) params.append('owner_id', user.userId);
      if (watchlistOnly) params.append('is_monitored', 'true');
      // The Orders tab lists orders; the Pipeline tab leaves them out.
      const kind = activeTab === 'orders' ? 'order' : 'opportunity';
      params.append('kind', kind);

      const summaryParams = new URLSearchParams({ open: 'true', kind });
      if (search) summaryParams.append('search', search);
      if (customerId) summaryParams.append('customer_id', customerId);
      if (stageFilter) summaryParams.append('stage', stageFilter);
      if (productFilter) summaryParams.append('product', productFilter);
      if (myDealsOnly && user?.userId) summaryParams.append('owner_id', user.userId);
      if (watchlistOnly) summaryParams.append('is_monitored', 'true');

      const [response, summaryResponse] = await Promise.all([
        authFetch(`/api/v1/opportunities?${params}`),
        authFetch(`/api/v1/opportunities/summary?${summaryParams}`)
      ]);
      const data = await response.json();
      setOpportunities(data.data || []);
      if (summaryResponse.ok) {
        setSummary(await summaryResponse.json());
      }
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
    } finally {
      setLoading(false);
    }
  }, [authFetch, activeTab, sortField, sortOrder, search, customerId, stageFilter, productFilter, myDealsOnly, watchlistOnly, user?.userId]);

  // Fetch grouped Opportunities (the commercial records)
  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const params = new URLSearchParams({ kind: activeTab === 'orders' ? 'order' : 'opportunity' });
      if (groupStatusFilter) params.append('status', groupStatusFilter);
      const response = await authFetch(`/api/v1/opportunity-groups?${params}`);
      if (!response.ok) {
        // A refused request must not read as "there is nothing here": before
        // this, a 403 fell through to data.data || [] and the page rendered
        // "0 opportunities" as if that were the truth.
        const body = await response.json().catch(() => ({}));
        setGroups([]);
        setPageError(body.message || 'Opportunities could not be loaded.');
        return;
      }
      const data = await response.json();
      setPageError(null);
      setGroups(data.data || []);
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
      setGroups([]);
      setPageError('Opportunities could not be loaded.');
    } finally {
      setGroupsLoading(false);
    }
  }, [authFetch, activeTab, groupStatusFilter]);

  useEffect(() => {
    if (activeTab === 'opportunities' || (activeTab === 'orders' && ordersView === 'orders')) {
      fetchGroups();
    }
  }, [activeTab, ordersView, fetchGroups]);

  // Fetch watchlist summary
  const fetchWatchlistSummary = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/watchlist/summary');
      const data = await response.json();
      setWatchlistSummary(data);
    } catch (error) {
      console.error('Failed to fetch watchlist summary:', error);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchStages();
    fetchWatchlistSummary();
  }, [fetchStages, fetchWatchlistSummary]);

  useEffect(() => {
    if (activeTab === 'list' || activeTab === 'orders') {
      fetchOpportunities();
    }
  }, [activeTab, fetchOpportunities]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchOpportunities();
        fetchWatchlistSummary();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchOpportunities, fetchWatchlistSummary]);

  const handleTabChange = (tab) => {
    // The Contracts Meeting filter starts at All each time that tab is
    // opened, as it did when the board held the filter itself.
    if (tab !== activeTab) setWatchlistFilter('all');
    setActiveTab(tab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams);
  };

  const handleSort = (field) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);

    const newParams = new URLSearchParams(searchParams);
    newParams.set('sort', field);
    newParams.set('order', newOrder);
    setSearchParams(newParams);
  };

  const handleStageChange = (opportunityId, newStage) => {
    applyStageChange(opportunityId, newStage);
  };

  const applyStageChange = async (opportunityId, newStage, extras = {}) => {
    const { stageChange = null, scheduleEntry = null } = extras;
    try {
      if (stageChange && !scheduleEntry) setStageDialogSubmitting(true);
      if (scheduleEntry) setScheduleSubmitting(true);
      const response = await authFetch(`/api/v1/opportunities/${opportunityId}/stage`, {
        method: 'PUT',
        body: JSON.stringify({
          stage: newStage,
          ...(stageChange ? { stage_change: { ...stageChange, source: 'pipeline' } } : {}),
          ...(scheduleEntry ? { schedule_entry: scheduleEntry } : {})
        })
      });

      if (response.ok) {
        setPageError(null);
        setStageDialog(null);
        setStageDialogError(null);
        setScheduleModal(null);
        setScheduleError(null);
        fetchOpportunities();
        fetchWatchlistSummary();
      } else {
        const data = await response.json().catch(() => ({}));
        const opp = opportunities.find(o => o.opportunity_id === opportunityId);
        if (data.code === 'STAGE_CHANGE_DETAILS_REQUIRED') {
          // Every stage change answers the accountability dialog first.
          if (stageChange) {
            setStageDialogError(data.message);
          } else {
            setStageDialogError(null);
            setStageDialog({
              opportunityId,
              stageName: newStage,
              lineLabel: opp ? `${opp.title || opp.opp_ref}` : '',
              details: data.details || {}
            });
          }
          return;
        }
        if (data.code === 'SCHEDULE_ENTRY_REQUIRED') {
          // Chains after the dialog — KEEP the stage_change payload so the
          // schedule modal's retry carries both.
          setStageDialog(null);
          if (scheduleEntry) {
            setScheduleError(data.message);
          } else {
            setScheduleError(null);
            setScheduleModal({
              opportunityId,
              stageName: newStage,
              lineLabel: opp ? `${opp.title || opp.opp_ref}` : '',
              demand: data.details || {},
              stageChange
            });
          }
          return;
        }
        setPageError(`Stage change failed: ${data.message || `HTTP ${response.status}`}`);
        setStageDialog(null);
        setScheduleModal(null);
        fetchOpportunities();
      }
    } catch (error) {
      setPageError('Stage change failed - check your connection and try again.');
      fetchOpportunities();
    } finally {
      setStageDialogSubmitting(false);
      setScheduleSubmitting(false);
    }
  };

  const handleRagChange = async (opportunityId, newPriority) => {
    try {
      const opp = opportunities.find(o => o.opportunity_id === opportunityId);
      let response;

      if (!newPriority) {
        // Clearing the RAG removes from the watchlist (and the API wipes the
        // monitor metadata so a later re-add starts fresh).
        response = await authFetch(`/api/v1/watchlist/${opportunityId}/remove`, { method: 'PUT' });
      } else if (opp && !opp.is_monitored) {
        // First enrolment goes through the proper add contract so
        // monitored_since / reason / review date are always populated.
        response = await authFetch(`/api/v1/watchlist/${opportunityId}/add`, {
          method: 'PUT',
          body: JSON.stringify({
            monitor_reason: `Quick-set from pipeline RAG (${newPriority})`,
            monitor_priority: newPriority,
            // A week from the viewer's today; toISOString() gave the UTC day
            monitor_review_date: toDateKey(addDays(new Date(), 7))
          })
        });
      } else {
        // Already enrolled with full metadata - just change the priority.
        response = await authFetch(`/api/v1/opportunities/${opportunityId}`, {
          method: 'PUT',
          body: JSON.stringify({ monitor_priority: newPriority })
        });
      }

      if (response.ok) {
        setOpportunities(prev => prev.map(o =>
          o.opportunity_id === opportunityId
            ? { ...o, is_monitored: newPriority !== '', monitor_priority: newPriority || null }
            : o
        ));
        fetchWatchlistSummary();
      } else {
        const data = await response.json().catch(() => ({}));
        setPageError(`Watchlist update failed: ${data.message || `HTTP ${response.status}`}`);
        fetchOpportunities();
      }
    } catch (error) {
      setPageError('Watchlist update failed - check your connection and try again.');
      fetchOpportunities();
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (search) {
      newParams.set('search', search);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  const formatCurrency = (minorUnits, currency = 'GBP') => formatMoney(minorUnits, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  // For instants (created_at). expected_close is a DATE: formatDateKey.
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB');
  };

  const formatPricingSummary = (opp) => {
    const summary = opp.pricing_summary || {};
    if (opp.opportunity_type === 'distributor' && summary.payment_milestones) {
      const upfront = ['on_order', 'on_ship', 'on_delivery', 'on_install']
        .reduce((sum, key) => sum + (Number(summary.payment_milestones[key]?.customer_pays) || 0), 0);
      const monthly = Number(summary.payment_milestones.monthly_service?.customer_pays) || 0;
      const label = `${formatCurrency(upfront, opp.currency)} upfront · ${formatCurrency(monthly, opp.currency)}/mo`;
      return summary.special_price ? `${label} · Special` : label;
    }
    if (opp.install_fee_minor_units || opp.monthly_invoice_amount_minor_units) {
      return `${formatCurrency(opp.install_fee_minor_units || opp.value_minor_units, opp.currency)} install · ${formatCurrency(opp.monthly_invoice_amount_minor_units, opp.currency)}/mo`;
    }
    return formatCurrency(opp.value_minor_units, opp.currency);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Stage-filter options follow the product filter; unfiltered shows the
  // de-duplicated union across all product types.
  const currentStages = (productFilter && stages[productFilter]) || allStages;

  // Pipeline stats come from the server summary: totals over ALL matching
  // opportunities (not just the fetched page), converted to GBP at the
  // admin-managed currency rates.
  const totalCount = summary ? summary.count : opportunities.length;
  const totalValue = summary ? Number(summary.total_gbp_minor) : 0;
  const weightedValue = summary ? Number(summary.weighted_gbp_minor) : 0;
  // DL-13: the second headline — what IDS keeps after the distributor take
  const exclTakeValue = summary ? Number(summary.total_excl_distributor_take_gbp_minor) : 0;
  const hasDistributorTake = exclTakeValue > 0 && exclTakeValue !== totalValue;
  const missingRates = summary?.currencies_missing_rate || [];

  // Product types with a configured stage list (pipelines are keyed by
  // product type — the old seven pipeline types are gone).
  const productTypes = Object.keys(stages).sort();

  return (
    <div className="pipeline-page">
      <div className="page-header">
        <div>
          <h1>Sales Pipeline</h1>
          {activeTab === 'list' ? (
            <p>
              {totalCount} line items · {formatCurrency(totalValue)} total
              {hasDistributorTake && <> · {formatCurrency(exclTakeValue)} excl. distributor take</>}
              {' · '}{formatCurrency(weightedValue)} weighted
              <span className="fx-note"> · converted at admin FX rates</span>
              {missingRates.length > 0 && (
                <span className="fx-warning"> · no rate configured for {missingRates.join(', ')} (counted 1:1)</span>
              )}
            </p>
          ) : activeTab === 'orders' ? (
            <p>
              {totalCount} order line{totalCount === 1 ? '' : 's'} · {formatCurrency(totalValue)} order book
              <span className="fx-note"> · converted at admin FX rates · not counted in the pipeline</span>
              {missingRates.length > 0 && (
                <span className="fx-warning"> · no rate configured for {missingRates.join(', ')} (counted 1:1)</span>
              )}
            </p>
          ) : activeTab === 'opportunities' ? (
            <p>{groups.length} opportunities · {formatCurrency(groups.reduce((sum, g) => sum + Number(g.computed_value_minor_units || 0), 0))} total value</p>
          ) : (
            <p>Line items flagged for contract review</p>
          )}
        </div>
        <div className="page-header-actions">
          {canCreate && (
            activeTab === 'orders' ? (
              <Link to="/pipeline/new?kind=order" className="btn-primary">+ New Order</Link>
            ) : (
              <Link to="/pipeline/new" className="btn-primary">+ New Opportunity</Link>
            )
          )}
        </div>
      </div>

      <div className="pipeline-tabs">
        <button
          className={activeTab === 'opportunities' ? 'tab active' : 'tab'}
          onClick={() => handleTabChange('opportunities')}
        >
          Opportunities
        </button>
        <button
          className={activeTab === 'list' ? 'tab active' : 'tab'}
          onClick={() => handleTabChange('list')}
        >
          Pipeline
        </button>
        <button
          className={activeTab === 'orders' ? 'tab active' : 'tab'}
          onClick={() => handleTabChange('orders')}
        >
          Orders
        </button>
        <button
          className={activeTab === 'watchlist' ? 'tab active' : 'tab'}
          onClick={() => handleTabChange('watchlist')}
        >
          Contracts Meeting
          {watchlistSummary && watchlistSummary.total_count > 0 && (
            <span className="tab-badge">{watchlistSummary.total_count}</span>
          )}
        </button>
      </div>

      {activeTab === 'orders' && (
        <div className="orders-view-switch" role="group" aria-label="Show whole orders or order lines">
          {[['orders', 'Whole orders'], ['lines', 'Order lines']].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={ordersView === value ? 'route-btn active' : 'route-btn'}
              aria-pressed={ordersView === value}
              onClick={() => setOrdersView(value)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {(activeTab === 'opportunities' || (activeTab === 'orders' && ordersView === 'orders')) && (
        <>
          <div className="pipeline-filters">
            <select
              value={groupStatusFilter}
              onChange={(e) => setGroupStatusFilter(e.target.value)}
              className="pipeline-select"
            >
              <option value="active">Active</option>
              <option value="complete">Complete</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <GroupsTable
            groups={groups}
            loading={groupsLoading}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            isOrders={activeTab === 'orders'}
          />
        </>
      )}

      {(activeTab === 'list' || (activeTab === 'orders' && ordersView === 'lines')) && (
        <div className="pipeline-filters">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              placeholder={activeTab === 'orders' ? 'Search orders...' : 'Search opportunities...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-btn">Search</button>
          </form>

          <select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className="pipeline-select"
          >
            <option value="">All Products</option>
            {productTypes.map(product => (
              <option key={product} value={product}>{String(product).replace(/_/g, ' ')}</option>
            ))}
          </select>

          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="pipeline-select"
          >
            <option value="">All Stages</option>
            {currentStages.map(stage => (
              <option key={stage.stage_name} value={stage.stage_name}>{stage.stage_name}</option>
            ))}
          </select>

          <label className="checkbox-filter">
            <input type="checkbox" checked={myDealsOnly} onChange={(e) => setMyDealsOnly(e.target.checked)} />
            My Deals
          </label>
          <label className="checkbox-filter">
            <input type="checkbox" checked={watchlistOnly} onChange={(e) => setWatchlistOnly(e.target.checked)} />
            Watchlist Only
          </label>
          <label className="checkbox-filter">
            <input type="checkbox" checked={groupByOpportunity} onChange={(e) => setGroupByOpportunity(e.target.checked)} />
            {activeTab === 'orders' ? 'Group by order' : 'Group by opportunity'}
          </label>
        </div>
      )}

      {pageError && (
        <div className="pipeline-page-error">
          {pageError}
          <button onClick={() => setPageError(null)}>&times;</button>
        </div>
      )}

      {(activeTab === 'list' || (activeTab === 'orders' && ordersView === 'lines')) && (
        <OpportunitiesTable
          opportunities={opportunities}
          setOpportunities={setOpportunities}
          loading={loading}
          stages={currentStages}
          stagesByPipeline={stages}
          onStageChange={handleStageChange}
          onRagChange={handleRagChange}
          onSort={handleSort}
          sortField={sortField}
          sortOrder={sortOrder}
          getSortIcon={getSortIcon}
          formatCurrency={formatCurrency}
          formatPricingSummary={formatPricingSummary}
          formatDate={formatDate}
          authFetch={authFetch}
          groupByOpportunity={groupByOpportunity}
          onMarkLost={(opp) => setLostModalItem(opp)}
          emptyText={activeTab === 'orders' ? 'No open orders' : 'No opportunities found'}
        />
      )}

      {lostModalItem && (
        <MarkLostModal
          item={lostModalItem}
          authFetch={authFetch}
          onClose={() => setLostModalItem(null)}
          onDone={() => {
            setLostModalItem(null);
            fetchOpportunities();
          }}
        />
      )}

      {activeTab === 'watchlist' && (
        <WatchlistBoard
          authFetch={authFetch}
          formatCurrency={formatCurrency}
          filter={watchlistFilter}
          onFilterChange={setWatchlistFilter}
        />
      )}

      {stageDialog && (
        <StageChangeDialog
          stageName={stageDialog.stageName}
          lineLabel={stageDialog.lineLabel}
          details={stageDialog.details}
          submitting={stageDialogSubmitting}
          submitError={stageDialogError}
          onCancel={() => {
            setStageDialog(null);
            setStageDialogError(null);
            fetchOpportunities();
          }}
          onSubmit={(stageChange) => applyStageChange(stageDialog.opportunityId, stageDialog.stageName, { stageChange })}
        />
      )}

      {scheduleModal && (
        <ScheduleEntryModal
          stageName={scheduleModal.stageName}
          lineLabel={scheduleModal.lineLabel}
          demand={scheduleModal.demand}
          submitting={scheduleSubmitting}
          submitError={scheduleError}
          onCancel={() => {
            setScheduleModal(null);
            setScheduleError(null);
            fetchOpportunities();
          }}
          onSubmit={(entry) => applyStageChange(scheduleModal.opportunityId, scheduleModal.stageName, {
            stageChange: scheduleModal.stageChange,
            scheduleEntry: entry
          })}
        />
      )}
    </div>
  );
}

/**
 * Sortable Opportunities Table
 */
function OpportunitiesTable({
  opportunities, setOpportunities, loading, stages, stagesByPipeline, onStageChange, onRagChange, onSort,
  sortField, getSortIcon, formatCurrency, formatPricingSummary, formatDate, authFetch,
  groupByOpportunity, onMarkLost, emptyText = 'No opportunities found'
}) {
  const { user } = useAuth();
  const canCreate = canManageDeals(user?.role);
  const canMoveStage = canMoveDealStage(user?.role);
  const [editingProbability, setEditingProbability] = useState(null);
  const [probabilityValue, setProbabilityValue] = useState('');
  const [probabilityError, setProbabilityError] = useState(null);

  const handleProbabilityEdit = (opp) => {
    setEditingProbability(opp.opportunity_id);
    setProbabilityValue(String(opp.probability_pct || 0));
    setProbabilityError(null);
  };

  const handleProbabilitySave = async (opportunityId) => {
    const value = parseInt(probabilityValue, 10);
    if (isNaN(value) || value < 0 || value > 100) {
      setProbabilityError('Must be 0-100');
      return;
    }

    try {
      const response = await authFetch(`/api/v1/opportunities/${opportunityId}/probability`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ probability_pct: value })
      });

      if (response.ok) {
        setOpportunities(prev => prev.map(opp =>
          opp.opportunity_id === opportunityId
            ? { ...opp, probability_pct: value }
            : opp
        ));
        setEditingProbability(null);
        setProbabilityError(null);
      } else {
        const data = await response.json();
        setProbabilityError(data.message || 'Failed to save');
      }
    } catch (error) {
      console.error('Failed to update probability:', error);
      setProbabilityError('Failed to save');
    }
  };

  const handleProbabilityKeyDown = (e, opportunityId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleProbabilitySave(opportunityId);
    } else if (e.key === 'Escape') {
      setEditingProbability(null);
      setProbabilityError(null);
    }
  };

  if (loading) {
    return <div className="loading">Loading pipeline...</div>;
  }

  if (opportunities.length === 0) {
    return <div className="empty-state">{emptyText}</div>;
  }

  return (
    <div className="pipeline-table-container">
      <table className="pipeline-table">
        <thead>
          <tr>
            <th onClick={() => onSort('monitor_priority')} className="sortable">
              RAG {getSortIcon('monitor_priority')}
            </th>
            <th onClick={() => onSort('opp_ref')} className="sortable">
              Ref {getSortIcon('opp_ref')}
            </th>
            <th onClick={() => onSort('title')} className="sortable">
              Opportunity {getSortIcon('title')}
            </th>
            <th onClick={() => onSort('customer_name')} className="sortable">
              End Customer {getSortIcon('customer_name')}
            </th>
            <th onClick={() => onSort('distributor_name')} className="sortable">
              Channel {getSortIcon('distributor_name')}
            </th>
            <th onClick={() => onSort('product')} className="sortable">
              Product {getSortIcon('product')}
            </th>
            <th onClick={() => onSort('stage')} className="sortable">
              Stage {getSortIcon('stage')}
            </th>
            <th onClick={() => onSort('owner')} className="sortable">
              Assigned To {getSortIcon('owner')}
            </th>
            <th onClick={() => onSort('value_minor_units')} className="sortable th-right">
              Pricing {getSortIcon('value_minor_units')}
            </th>
            <th onClick={() => onSort('probability_pct')} className="sortable th-right">
              Prob {getSortIcon('probability_pct')}
            </th>
            <th onClick={() => onSort('expected_close')} className="sortable">
              Expected Close {getSortIcon('expected_close')}
            </th>
            <th onClick={() => onSort('created_at')} className="sortable">
              Created {getSortIcon('created_at')}
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(groupByOpportunity
            ? groupRows(opportunities).flatMap(({ key, groupRef, groupTitle, rows }) => [
              <tr key={`header-${key}`} className="group-header-row">
                <td colSpan={13}>
                  {groupRef ? (
                    <Link to={`/pipeline/groups/${key}`}>
                      <strong>{groupRef}</strong> — {groupTitle}
                    </Link>
                  ) : <strong>Ungrouped</strong>}
                  <span className="group-header-total">
                    {rows.length} item{rows.length === 1 ? '' : 's'} · {formatCurrency(rows.reduce((sum, row) => sum + Number(row.value_minor_units || 0), 0), rows[0]?.currency)}
                  </span>
                </td>
              </tr>,
              ...rows.map(opp => renderRow(opp))
            ])
            : opportunities.map(opp => renderRow(opp)))}
        </tbody>
      </table>
    </div>
  );

  function groupRows(rows) {
    const map = new Map();
    for (const row of rows) {
      const key = row.opportunity_group_id || 'none';
      if (!map.has(key)) {
        map.set(key, { key, groupRef: row.group_ref, groupTitle: row.group_title, rows: [] });
      }
      map.get(key).rows.push(row);
    }
    return [...map.values()];
  }

  function renderRow(opp) {
    const stageOptions = stagesForLine(stagesByPipeline?.[opp.product] || stages, opp);
    const isEditingThisProb = editingProbability === opp.opportunity_id;

    return (
            <tr key={opp.opportunity_id}>
              <td className="td-rag">
                <select
                  value={opp.monitor_priority || ''}
                  onChange={(e) => onRagChange(opp.opportunity_id, e.target.value)}
                  className={`rag-select ${opp.monitor_priority || 'none'}`}
                  disabled={!canCreate}
                >
                  <option value="">—</option>
                  <option value="green">Green</option>
                  <option value="amber">Amber</option>
                  <option value="red">Red</option>
                </select>
              </td>
              <td className="td-ref">
                <Link to={`/pipeline/${opp.opportunity_id}`}>
                  {opp.opp_ref}
                </Link>
                {opp.is_order && (
                  <div className="order-row-tags">
                    <span className="order-badge">Order</span>
                    {!opp.order_accepted_at && <span className="order-not-accepted">Not accepted</span>}
                  </div>
                )}
              </td>
              <td className="td-title">
                <Link to={`/pipeline/${opp.opportunity_id}`}>
                  {opp.title}
                </Link>
                {opp.description && <div className="line-what">{opp.description}</div>}
              </td>
              <td className="td-customer">
                <Link to={`/customers/${opp.customer_id}`}>
                  {opp.customer_name}
                </Link>
              </td>
              <td className="td-channel">
                <span className={`channel-badge ${opp.opportunity_type || 'direct'}`}>
                  {opp.opportunity_type === 'distributor' ? 'Distributor' : 'Direct'}
                </span>
                {opp.opportunity_type === 'distributor' && opp.distributor_name && (
                  <Link to={`/customers/${opp.distributor_id || opp.master_distributor_id}`} className="channel-distributor">
                    {opp.distributor_name}
                  </Link>
                )}
              </td>
              <td className="td-product">
                <span className="product-badge">
                  {opp.product?.replace(/_/g, ' ')}
                </span>
              </td>
              <td className="td-stage">
                <select
                  value={opp.stage}
                  onChange={(e) => onStageChange(opp.opportunity_id, e.target.value)}
                  className="stage-select"
                  disabled={!canMoveStage || opp.approval_status === 'pending' || opp.approval_status === 'rejected'}
                  title={opp.approval_status === 'pending'
                    ? 'Awaiting approval — cannot progress yet'
                    : opp.approval_status === 'rejected'
                      ? 'Rejected — revise the pricing first'
                      : undefined}
                >
                  {stageOptions.map(s => (
                    <option key={s.stage_name} value={s.stage_name}>
                      {s.stage_name}
                    </option>
                  ))}
                </select>
                {opp.approval_status === 'pending' && (
                  <span className="approval-badge pending" title="Special pricing awaiting approval">Awaiting Approval</span>
                )}
                {opp.approval_status === 'rejected' && (
                  <Link to={`/pipeline/groups/${opp.opportunity_group_id}`} className="approval-badge rejected" title="Pricing rejected — action required">
                    Action Required
                  </Link>
                )}
                {opp.won_at && (
                  <span className="won-chip" title="Won — progressing through post-won stages until installation completes">
                    Won
                  </span>
                )}
              </td>
              <td className="td-owner">
                {opp.owner_group_name ? (
                  <span className="owner-group" title={`Owned by the ${opp.owner_group_name} group`}>
                    {opp.owner_group_name} <span className="owner-group-tag">group</span>
                  </span>
                ) : (
                  <span title={opp.owner_name || undefined}>{opp.owner_name || '—'}</span>
                )}
              </td>
              <td className="td-value">
                {formatPricingSummary(opp)}
              </td>
              <td className="td-probability">
                {isEditingThisProb ? (
                  <div className="prob-edit-wrapper">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={probabilityValue}
                      onChange={(e) => setProbabilityValue(e.target.value)}
                      onKeyDown={(e) => handleProbabilityKeyDown(e, opp.opportunity_id)}
                      onBlur={() => handleProbabilitySave(opp.opportunity_id)}
                      className={`prob-input ${probabilityError ? 'error' : ''}`}
                      autoFocus
                    />
                    {probabilityError && <span className="prob-error">{probabilityError}</span>}
                  </div>
                ) : (
                  <span
                    className={`prob-badge editable ${opp.probability_pct >= 75 ? 'high' : opp.probability_pct >= 50 ? 'medium' : 'low'}`}
                    onClick={() => canCreate && handleProbabilityEdit(opp)}
                    title="Click to edit probability"
                  >
                    {opp.probability_pct}%
                  </span>
                )}
              </td>
              <td className="td-date">
                {formatDateKey(opp.expected_close)}
              </td>
              <td className="td-date">
                {formatDate(opp.created_at)}
              </td>
              <td className="td-actions">
                {!opp.won_at && !opp.lost_at && (
                  <button
                    type="button"
                    className="mark-lost-btn"
                    title="Mark this line item lost (requires a reason)"
                    onClick={() => onMarkLost(opp)}
                  >
                    Lost
                  </button>
                )}
              </td>
            </tr>
    );
  }
}

/**
 * Grouped Opportunities tab — the commercial records (spec §20).
 */
function GroupsTable({ groups, loading, formatCurrency, formatDate, isOrders = false }) {
  if (loading) {
    return <div className="loading">{isOrders ? 'Loading orders...' : 'Loading opportunities...'}</div>;
  }
  if (groups.length === 0) {
    return <div className="empty-state">{isOrders ? 'No orders found' : 'No opportunities found'}</div>;
  }

  return (
    <div className="pipeline-table-container">
      <table className="pipeline-table">
        <thead>
          <tr>
            <th>Ref</th>
            <th>{isOrders ? 'Order' : 'Opportunity'}</th>
            <th>Customer</th>
            <th>Route</th>
            <th>Items</th>
            <th className="th-right">Value</th>
            {isOrders && <th>Accepted</th>}
            <th>Owner</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {groups.map(group => (
            <tr key={group.group_id}>
              <td className="td-ref">
                <Link to={`/pipeline/groups/${group.group_id}`}>{group.group_ref}</Link>
                {group.status === 'complete' && group.outcome && OUTCOME_META[group.outcome] && (
                  <span className={`chip ${OUTCOME_META[group.outcome].cls}`} style={{ marginLeft: 6 }}>
                    {OUTCOME_META[group.outcome].label}
                  </span>
                )}
              </td>
              <td className="td-title">
                <Link to={`/pipeline/groups/${group.group_id}`}>{group.title}</Link>
              </td>
              <td className="td-customer">
                <Link to={`/customers/${group.customer_id}`}>{group.customer_name}</Link>
              </td>
              <td className="td-channel">
                <span className={`channel-badge ${group.route}`}>
                  {group.route === 'distributor' ? 'Distributor' : 'Direct'}
                </span>
                {group.route === 'distributor' && group.distributor_name && (
                  <span className="channel-distributor">{group.distributor_name}</span>
                )}
              </td>
              <td>{group.item_count}</td>
              <td className="td-value">{formatCurrency(Number(group.computed_value_minor_units || 0))}</td>
              {isOrders && (
                <td>
                  {group.order_accepted_at
                    ? `${formatDate(group.order_accepted_at)}${group.order_accepted_by_name ? ` · ${group.order_accepted_by_name}` : ''}`
                    : <span className="order-not-accepted">Not accepted</span>}
                </td>
              )}
              <td>{group.owner_name || '—'}</td>
              <td className="td-date">{formatDate(group.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Watchlist Board Component
 */
function WatchlistBoard({ authFetch, formatCurrency, filter, onFilterChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWatchlist = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filter !== 'all') {
          params.append('priority', filter);
        }
        const response = await authFetch(`/api/v1/watchlist?${params}`);
        const data = await response.json();
        setItems(data.data || []);
      } catch (error) {
        console.error('Failed to fetch watchlist:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchWatchlist();
  }, [authFetch, filter]);

  if (loading) {
    return <div className="loading">Loading watchlist...</div>;
  }

  return (
    <div className="watchlist-board">
      <div className="watchlist-filters">
        <button
          className={filter === 'all' ? 'filter-btn active' : 'filter-btn'}
          onClick={() => onFilterChange('all')}
        >
          All ({items.length})
        </button>
        <button
          className={filter === 'red' ? 'filter-btn active red' : 'filter-btn red'}
          onClick={() => onFilterChange('red')}
        >
          Red
        </button>
        <button
          className={filter === 'amber' ? 'filter-btn active amber' : 'filter-btn amber'}
          onClick={() => onFilterChange('amber')}
        >
          Amber
        </button>
        <button
          className={filter === 'green' ? 'filter-btn active green' : 'filter-btn green'}
          onClick={() => onFilterChange('green')}
        >
          Green
        </button>
      </div>

      {items.length === 0 ? (
        <div className="empty-state">No items on watchlist</div>
      ) : (
        <div className="watchlist-table">
          <table>
            <thead>
              <tr>
                <th>Priority</th>
                <th>Opportunity</th>
                <th>Customer</th>
                <th>Stage</th>
                <th>Value</th>
                <th>Next-action owner</th>
                <th>Next step</th>
                <th>Target / Revised close</th>
                <th>Delay / hold / lost</th>
                <th>Review Date</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.opportunity_id} className={item.review_overdue ? 'overdue' : ''}>
                  <td>
                    <span className={`priority-badge ${item.monitor_priority}`}>
                      {item.monitor_priority}
                    </span>
                  </td>
                  <td>
                    <Link to={`/pipeline/${item.opportunity_id}`} className="opp-link">
                      {item.title}
                    </Link>
                    <br />
                    <span className="opp-ref-small">{item.opp_ref}</span>
                    {item.monitor_reason && (
                      <div className="watch-reason" title={item.monitor_reason}>{item.monitor_reason}</div>
                    )}
                  </td>
                  <td>{item.customer_name}</td>
                  <td>{item.stage}</td>
                  <td>{formatCurrency(item.value_minor_units, item.currency)}</td>
                  <td>{item.monitor_assigned_to_name || '—'}</td>
                  <td className="next-step">{item.monitor_next_step || item.last_action_agreed || '—'}</td>
                  <td className="close-timing">
                    {formatDateKey(item.expected_close, undefined, '—')}
                    {item.revised_expected_close && (
                      <div className="revised" title={item.revised_close_reason || ''}>
                        → {formatDateKey(item.revised_expected_close)}
                        {item.revised_close_reason ? ` (${item.revised_close_reason})` : ''}
                      </div>
                    )}
                  </td>
                  <td className="watch-reasons">
                    {item.delay_reason && <div>Delay: {item.delay_reason}</div>}
                    {item.hold_reason && <div>Hold: {item.hold_reason}</div>}
                    {item.lost_reason && <div>Lost: {item.lost_reason}</div>}
                    {!item.delay_reason && !item.hold_reason && !item.lost_reason && '—'}
                  </td>
                  <td className={item.review_overdue ? 'overdue-date' : ''}>
                    {formatDateKey(item.monitor_review_date)}
                    {item.days_on_watchlist != null && <div className="days-small">{item.days_on_watchlist}d on watch</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Pipeline;
